import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

// ✅ Format helper
const fmt = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const safe = (v) => v || "-";
const money = (v) => "₹" + Number(v || 0).toFixed(2);

const todayYYYYMMDD = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ✅ Helper to robustly check pending status
const checkPending = (t) => {
  if (t?.pending === true) return true;
  let m = t?.meta;
  if (typeof m === "string" && m.startsWith("{")) {
    try { m = JSON.parse(m); } catch { m = null; }
  }
  if (m?.pending === true) return true;
  return false;
};

// ✅ Badge helper
const StatusBadge = ({ pending }) => (
  <span className={`di-pill ${pending ? "warn" : "ok"}`}>
    {pending ? "PENDING" : "CREDITED"}
  </span>
);

function TxnList({ rows }) {
  const list = Array.isArray(rows) ? rows : [];

  if (list.length === 0) return <div className="di-empty">No transactions found</div>;

  return (
    <div className="di-listSection">
      {/* Desktop Table */}
      <div className="di-tableWrap">
        <table className="di-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={`t-${t?.id ?? Math.random()}`}>
                <td>#{safe(t?.id)}</td>
                <td>₹{Number(t?.amount || 0).toFixed(2)}</td>
                <td>
                  <StatusBadge pending={checkPending(t)} />
                </td>
                <td>{fmt(t?.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="di-mCards">
        {list.map((t) => (
          <div className="di-mCard" key={`m-${t?.id ?? `${t?.createdAt}-${Math.random()}`}`}>
            <div className="di-mTop">
              <div className="di-mId">#{safe(t?.id)}</div>
              <StatusBadge pending={checkPending(t)} />
            </div>

            <div className="di-mAmount">₹{Number(t?.amount || 0).toFixed(2)}</div>

            <div className="di-mMeta">
              <div className="di-mMetaRow" style={{ marginTop: 8 }}>
                <span className="di-mK">Created at:</span>
                <span className="di-mV">{fmt(t?.createdAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DailyIncomeReport() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // form
  const [date, setDate] = useState(todayYYYYMMDD());
  const [timezone, setTimezone] = useState("Asia/Kolkata");

  // data
  const [report, setReport] = useState(null);

  // ✅ ensure header on refresh
  useEffect(() => {
    if (token) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    fetchReport(todayYYYYMMDD(), "Asia/Kolkata");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async (d, tz) => {
    try {
      setErr("");
      setLoading(true);

      const res = await axiosInstance.get("/api/reports/daily-income", {
        params: { date: d, timezone: tz },
      });

      setReport(res?.data || null);
    } catch (e) {
      console.log("daily-income error", e);
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        navigate("/login", { replace: true });
      } else {
        setErr(e?.response?.data?.msg || "Failed to load daily income report");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    fetchReport(date, timezone);
  };

  const pairTxns = useMemo(() => report?.recent?.pairTxns || [], [report]);
  const joinTxns = useMemo(() => report?.recent?.joinTxns || [], [report]);

  // ✅ Recalculate totals
  const totalsRecalc = useMemo(() => {
    const pair = { credited: 0, pending: 0, count: 0 };
    const join = { credited: 0, pending: 0, count: 0 };

    pairTxns.forEach(t => {
      const isPending = checkPending(t);
      const amt = Number(t?.amount || 0);
      if (isPending) pair.pending += amt;
      else pair.credited += amt;
      pair.count++;
    });

    joinTxns.forEach(t => {
      const isPending = checkPending(t);
      const amt = Number(t?.amount || 0);
      if (isPending) join.pending += amt;
      else join.credited += amt;
      join.count++;
    });

    const grandCredited = pair.credited + join.credited;
    const grandPending = pair.pending + join.pending;

    return {
      pair: { ...pair, total: pair.credited + pair.pending },
      join: { ...join, total: join.credited + join.pending },
      grandCredited,
      grandPending,
      grandTotal: grandCredited + grandPending,
    };
  }, [pairTxns, joinTxns]);

  const pairIncome = totalsRecalc.pair;
  const joinIncome = totalsRecalc.join;
  const totals = {
    grandTotal: totalsRecalc.grandTotal,
    credited: totalsRecalc.grandCredited,
    pending: totalsRecalc.grandPending
  };

  const totalTxnCount = pairIncome.count + joinIncome.count;

  return (
    <div className="di-page">
      <div className="di-container">
        <style>{css}</style>

        {/* TOP BAR */}
        <div className="di-topbar">
          <div className="di-head">
            <div className="di-title">Daily Income Report</div>
            <div className="di-subtitle">Pair income + joining income (daily)</div>
          </div>
        </div>

        {/* FILTER CARD */}
        <div className="di-card">
          <div className="di-cardTitle">Filter</div>

          <form onSubmit={onSubmit} className="di-form">
            <div className="di-field">
              <label className="di-label">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="di-input"
                disabled={loading}
              />
            </div>

            <div className="di-field btnWrap">
              <button type="submit" className="di-btnPrimary" disabled={loading}>
                {loading ? "Loading..." : "Get Report"}
              </button>
            </div>
          </form>

          {err ? <div className="di-error" style={{marginTop:12, color:'red'}}>{err}</div> : null}
        </div>

        {/* REPORT */}
        <div className="di-card">
          {loading ? (
            <div className="di-info">Loading...</div>
          ) : !report ? (
            <div className="di-info">No data found</div>
          ) : (
            <>
              <div className="di-meta">
                Date: <b>{safe(report?.date)}</b> • Timezone: <b>{safe(report?.timezone)}</b>
              </div>

              {/* KPI */}
              <div className="di-kpiGrid">
                <div className="di-kpi">
                  <div className="di-kLabel">💰 Total Income</div>
                  <div className="di-kValue">{money(totals?.grandTotal)}</div>
                  <div className="di-kHint">
                    Credited: <b>{money(totals?.credited)}</b> • Pending: <b>{money(totals?.pending)}</b>
                  </div>
                </div>

                <div className="di-kpi">
                  <div className="di-kLabel">👥 Pairs matched</div>
                  <div className="di-kValue">{Number(report?.pairsMatched || 0)}</div>
                  <div className="di-kHint">
                    Flushed: <b>{Number(report?.flushedPairs || 0)}</b>
                  </div>
                </div>

                <div className="di-kpi">
                  <div className="di-kLabel">📑 Transactions</div>
                  <div className="di-kValue">{totalTxnCount}</div>
                  <div className="di-kHint">
                    Pair: <b>{Number(pairIncome?.count || 0)}</b> • Join:{" "}
                    <b>{Number(joinIncome?.count || 0)}</b>
                  </div>
                </div>
              </div>

              <div className="di-hr" />

              {/* BREAKDOWN */}
              <div style={{ height: 20 }} />
              <div className="di-cardTitle">📊 Income Breakdown</div>

              <div className="di-breakGrid">
                <div className="di-panel">
                  <div className="di-panelTitle">👬 Pair Income</div>
                  <div className="di-line"> <span className="di-k">Credited</span> <b>{money(pairIncome?.credited)}</b> </div>
                  <div className="di-line"> <span className="di-k">Pending</span> <b>{money(pairIncome?.pending)}</b> </div>
                  <div className="di-line"> <span className="di-k">Total</span> <b>{money(pairIncome?.total)}</b> </div>
                  <div className="di-line"> <span className="di-k">Txns</span> <b>{Number(pairIncome?.count || 0)}</b> </div>
                </div>

                <div className="di-panel">
                  <div className="di-panelTitle">🚀 Joining Income</div>
                  <div className="di-line"> <span className="di-k">Credited</span> <b>{money(joinIncome?.credited)}</b> </div>
                  <div className="di-line"> <span className="di-k">Pending</span> <b>{money(joinIncome?.pending)}</b> </div>
                  <div className="di-line"> <span className="di-k">Total</span> <b>{money(joinIncome?.total)}</b> </div>
                  <div className="di-line"> <span className="di-k">Txns</span> <b>{Number(joinIncome?.count || 0)}</b> </div>
                </div>
              </div>

              <div className="di-hr" />

              {/* RECENTS */}
              <div className="di-cardTitle">Recent Pair Transactions</div>
              <TxnList rows={pairTxns} />

              <div style={{ height: 32 }} />

              <div className="di-cardTitle">Recent Joining Transactions</div>
              <TxnList rows={joinTxns} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const css = `
  :root{
    --bg: #f4f6f8;
    --card: #ffffff;
    --text: #0f172a;
    --muted: rgba(15,23,42,.70);
    --line: rgba(15,23,42,.10);
    --primary: #006600;
  }

  * { box-sizing: border-box; }

  .di-page{
    min-height: 100vh;
    background: var(--bg);
    padding: 16px 12px 100px;
    width: 100%;
    overflow-x: hidden;
  }
  .di-container{
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    overflow-x: hidden;
  }

  .di-topbar{ margin-bottom: 20px; width: 100%; }
  .di-title{ font-size: 24px; font-weight: 1000; color: #0f172a; word-wrap: break-word; }
  .di-subtitle{ font-size: 14px; color: #64748b; font-weight: 800; margin-top: 2px; word-wrap: break-word; }

  .di-kpiGrid{ display: grid; grid-template-columns: 1fr; gap: 16px; }
  .di-kpi{
    background: #fff;
    border-radius: 18px;
    padding: 20px;
    border: 1px solid rgba(0,0,0,0.04);
    box-shadow: 0 10px 25px rgba(0,0,0,0.03);
  }
  .di-kpi:nth-child(1){ border-left: 6px solid #006600; }
  .di-kpi:nth-child(2){ border-left: 6px solid #10b981; }
  .di-kpi:nth-child(3){ border-left: 6px solid #7c3aed; }

  .di-kLabel{ font-size: 11px; font-weight: 1000; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .di-kValue{ font-size: 22px; font-weight: 1100; color: #1e293b; margin: 6px 0; }
  .di-kHint{ font-size: 11px; color: #64748b; font-weight: 800; }

  .di-card{
    background: #fff;
    border-radius: 20px;
    padding: 20px;
    border: 1px solid rgba(0,0,0,0.06);
    box-shadow: 0 10px 30px rgba(0,0,0,0.04);
    margin-bottom: 24px;
    width: 100%;
    max-width: 100%;
  }
  .di-cardTitle{ 
    font-size: 14px; font-weight: 1000; color: #475569; margin-bottom: 18px; 
    text-transform: uppercase; letter-spacing: 1px; display: flex; align-items: center; gap: 8px;
    word-wrap: break-word;
  }
  .di-meta{ 
    font-size: 12px; font-weight: 800; color: #64748b; background: #f8fafc; 
    padding: 10px 15px; border-radius: 12px; margin-bottom: 16px; display: block; width: fit-content; max-width: 100%; word-wrap: break-word;
  }

  .di-form{ display: grid; grid-template-columns: 1fr; gap: 16px; }
  .di-field{ display: flex; flex-direction: column; }
  .di-label{ font-size: 13px; font-weight: 1000; color: #475569; margin-bottom: 8px; }
  .di-input{
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    font-weight: 900;
    outline: none;
    transition: 0.2s;
  }
  .di-input:focus{ border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
  
  .di-btnPrimary{
    background: #006600;
    color: #fff;
    border: none;
    border-radius: 12px;
    padding: 14px;
    font-weight: 1000;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(0,102,0,0.25);
    transition: 0.2s;
  }
  .di-btnPrimary:hover{ background: #004d00; }
  .di-btnPrimary:active{ transform: scale(0.98); }

  .di-breakGrid{ display: grid; grid-template-columns: 1fr; gap: 16px; }
  .di-panel{ border-radius: 18px; padding: 20px; background: #fff; border: 1px solid rgba(0,0,0,0.05); }
  .di-panelTitle{ font-size: 15px; font-weight: 1000; color: #1e293b; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
  .di-line{ display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px dashed #f1f5f9; font-size: 14px; }
  .di-line:last-child{ border: none; }
  .di-k{ font-weight: 900; color: #64748b; }

  .di-pill{ padding: 6px 12px; border-radius: 10px; font-size: 10px; font-weight: 1000; border: 1px solid transparent; text-transform: uppercase; margin-bottom: 4px; display: inline-block; }
  .di-pill.ok{ background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
  .di-pill.warn{ background: #fee2e2; color: #b91c1c; border-color: #fecaca; }

  .di-mCard{ border-radius: 18px; background: #fff; border: 1px solid #f1f5f9; padding: 18px; margin-bottom: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.02); }
  .di-mTop{ display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
  .di-mId{ font-weight: 1000; color: #0f172a; }
  .di-mAmount{ font-size: 20px; font-weight: 1100; color: #006600; margin-bottom: 10px; }
  .di-mMeta{ border-top: 1px solid #f1f5f9; padding-top: 10px; }
  .di-mMetaRow{ display: flex; justify-content: space-between; font-size: 12px; }
  .di-mK{ color: #64748b; font-weight: 900; }
  .di-mV{ color: #0f172a; font-weight: 800; }

  .di-hr{ height: 1px; background: #f1f5f9; margin: 24px 0; }
  .di-tableWrap{ display: none; }
  .di-empty{ padding: 20px; color: #64748b; font-weight: 800; text-align: center; }

  @media (min-width: 640px){
    .di-kpiGrid{ grid-template-columns: 1fr 1fr; }
    .di-form{ grid-template-columns: 320px 160px; column-gap: 16px; align-items: flex-end; }
    .btnWrap{ padding-bottom: 2px; }
  }
  @media (min-width: 1024px){
    .di-kpiGrid{ grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .di-breakGrid{ grid-template-columns: 1fr 1fr; gap: 24px; }
    .di-form{ grid-template-columns: 320px 180px; }
    .di-tableWrap{ display: block; overflow-x: auto; border-radius: 16px; border: 1px solid #f1f5f9; box-shadow: 0 4px 12px rgba(0,0,0,0.02); }
    .di-mCards{ display: none; }
    .di-table{ width: 100%; border-collapse: collapse; }
    .di-table th{ background: #f8fafc; color: #64748b; padding: 14px; text-align: left; font-size: 11px; text-transform: uppercase; border-bottom: 1px solid #f1f5f9; }
    .di-table td{ padding: 14px; font-size: 13px; border-bottom: 1px solid #f8fafc; font-weight: 800; }
  }
`;